/**
 * HotspotActivityCard — W4.7 / S10-10
 *
 * Last Beacon, Last PoC Activity, Last Unselected.
 * Nutzt date-fns für menschenlesbare relative Zeit.
 *
 * S10-10 Color-Coding:
 * - >24h  → red/danger
 * - >6h   → yellow/warning
 * - ≤6h   → green/success
 * - null  → "Nie"
 *
 * heliumtracker-web#135: Helium hat Proof-of-Coverage am 06.07.2026 netzweit
 * abgeschaltet. Beacon- und Unselected-Zeitstempel bleiben seitdem für jeden
 * Hotspot dauerhaft stehen — sie sind historische Endpunkte, keine "letzte
 * Aktivität". Diese Zeilen werden deshalb nicht mehr rot/gelb eingefärbt,
 * sondern neutral mit Hinweis dargestellt. Lebenszeichen und Heartbeat haben
 * andere Quellen (Packetreport/Rewards bzw. Mobile) und bleiben bewertet.
 */

/** Netzweite PoC-Abschaltung (UTC-Tagesende). Werte danach gibt es nicht mehr. */
const POC_SHUTDOWN_AT = new Date('2026-07-06T23:59:59Z')

function isPocHistorical(iso: string | null | undefined): boolean {
  if (!iso) return false
  const date = new Date(iso)
  return !Number.isNaN(date.getTime()) && date <= POC_SHUTDOWN_AT
}
import { formatDistanceToNow, differenceInHours } from 'date-fns'
import { useTranslation } from 'react-i18next'
import type { HotspotDetail } from './useHotspotDetail'
import { activeDateFnsLocale, formatLocalDateTime, formatMinutesAgo } from '../../lib/locale'

// --- Hilfsfunktionen ---

export type ActivityColor = 'danger' | 'warning' | 'success'

function getActivityColor(iso: string | null | undefined): ActivityColor | null {
  if (!iso) return null
  try {
    const date = new Date(iso)
    const hoursAgo = differenceInHours(new Date(), date)
    if (hoursAgo > 24) return 'danger'
    if (hoursAgo > 6) return 'warning'
    return 'success'
  } catch {
    return null
  }
}

function relativeTime(iso: string | null | undefined, neverLabel: string): string {
  if (!iso) return neverLabel
  try {
    const date = new Date(iso)
    return formatDistanceToNow(date, { addSuffix: true, locale: activeDateFnsLocale() })
  } catch {
    return iso
  }
}

/**
 * Issue #124 (= #55): nutzt den zentralen Formatter aus lib/locale.ts, der
 * die Konto-Einstellungen date_format/time_format beachtet (Settings ->
 * Display) — vorher fest `toLocaleDateString(activeBcp47(), ...)` ohne
 * hour12-Option, wodurch im EN-Modus immer 12h/AM-PM erschien, egal was
 * unter "Time format" eingestellt war.
 */
function formatDate(iso: string | null | undefined): string {
  if (!iso) return ''
  const formatted = formatLocalDateTime(iso)
  return formatted === '—' ? '' : formatted
}

// --- ActivityRow ---

type ActivityRowProps = {
  label: string
  iso: string | null | undefined
  neverLabel: string
  /** #135: PoC-abhängiger Wert — neutral darstellen, Hinweis statt Warnfarbe */
  historical?: boolean
  historicalNote?: string
}

const colorMap: Record<ActivityColor, string> = {
  danger: 'var(--danger)',
  warning: 'var(--warning)',
  success: 'var(--success)',
}

function ActivityRow({
  label,
  iso,
  neverLabel,
  historical = false,
  historicalNote,
}: ActivityRowProps) {
  const relative = relativeTime(iso, neverLabel)
  const absolute = formatDate(iso)
  const color = historical ? null : getActivityColor(iso)

  const textColor = color ? colorMap[color] : 'inherit'

  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        gap: 12,
        padding: '8px 0',
        borderBottom: '1px solid var(--border)',
      }}
    >
      <span className="muted" style={{ fontSize: 12, flexShrink: 0 }}>
        {label}
      </span>
      <div style={{ textAlign: 'right' }}>
        {/* S10-10: title-Attribut mit dem rohen ISO-String, nur wenn ein Wert vorhanden ist */}
        <div style={{ fontSize: 13, fontWeight: 500, color: textColor }} title={iso ?? undefined}>
          {relative}
        </div>
        {absolute && (
          <div className="muted" style={{ fontSize: 11, marginTop: 1 }}>
            {absolute}
          </div>
        )}
        {historical && historicalNote && (
          <div
            className="muted"
            style={{ fontSize: 11, marginTop: 2 }}
            data-testid="activity-poc-historical"
          >
            {historicalNote}
          </div>
        )}
      </div>
    </div>
  )
}

// --- Props ---

type Props = {
  hotspot: HotspotDetail
}

export function HotspotActivityCard({ hotspot }: Props) {
  const { t } = useTranslation('hotspots')
  const neverLabel = t('activity.never')
  const pocNote = t('activity.pocShutdownNote')
  return (
    <div className="card card-pad" aria-label={t('activity.cardLabel')}>
      <h3 className="section-heading" style={{ marginBottom: 12 }}>
        {t('activity.heading')}
      </h3>

      <ActivityRow
        label={t('activity.labelBeacon')}
        iso={hotspot.last_beacon_at}
        neverLabel={neverLabel}
        historical={isPocHistorical(hotspot.last_beacon_at)}
        historicalNote={pocNote}
      />
      <ActivityRow
        label={t('activity.labelPoc')}
        iso={hotspot.last_poc_activity_at}
        neverLabel={neverLabel}
      />
      <ActivityRow
        label={t('activity.labelUnselected')}
        iso={hotspot.last_unselected_at}
        neverLabel={neverLabel}
        historical={isPocHistorical(hotspot.last_unselected_at)}
        historicalNote={pocNote}
      />

      {/* Stream H (R4): Heartbeat-Abstand in Minuten (v1: poc_dif). Kommt als
          Zahl, nicht als Zeitstempel — deshalb keine ActivityRow. */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 12,
          padding: '8px 0',
        }}
        data-testid="activity-heartbeat-row"
      >
        <span className="muted" style={{ fontSize: 12 }}>
          {t('activity.labelHeartbeat')}
        </span>
        <span style={{ fontSize: 13, fontWeight: 500 }}>
          {formatMinutesAgo(hotspot.heartbeat_minutes_ago) ?? neverLabel}
        </span>
      </div>
    </div>
  )
}
